/**
 * useLogoUpload Hook - Manages logo upload operations
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../services/apiClient';
import { msg, MESSAGES } from '../utils/messages';
import { useConfirm } from './useConfirm';

interface UseLogoUploadReturn {
  // Main logo
  logoPreview: string | null;
  currentLogo: string | null;
  isUploadingLogo: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleLogoFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteLogo: () => Promise<void>;

  // Mini logo
  miniLogoPreview: string | null;
  currentMiniLogo: string | null;
  isUploadingMiniLogo: boolean;
  miniLogoFileInputRef: React.RefObject<HTMLInputElement>;
  handleMiniLogoFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteMiniLogo: () => Promise<void>;

  // Confirm dialog
  ConfirmDialog: ReturnType<typeof useConfirm>['ConfirmDialog'];
}

// Update favicon helper function
const updateFavicon = (url: string | null) => {
  const favicon = document.getElementById('favicon') as HTMLLinkElement;
  if (favicon) {
    favicon.href = url || '/logo-mini-default.png';
    console.log('✅ Favicon atualizado:', favicon.href);
  }
};

export function useLogoUpload(): UseLogoUploadReturn {
  const { confirm, ConfirmDialog } = useConfirm();

  // Main logo state
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [currentLogo, setCurrentLogo] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mini logo state
  const [miniLogoPreview, setMiniLogoPreview] = useState<string | null>(null);
  const [currentMiniLogo, setCurrentMiniLogo] = useState<string | null>(null);
  const [isUploadingMiniLogo, setIsUploadingMiniLogo] = useState(false);
  const miniLogoFileInputRef = useRef<HTMLInputElement>(null);

  // Fetch system logos on mount
  const fetchSystemLogo = useCallback(async () => {
    try {
      const response = await apiClient.get('/config/system');
      const data = response.data;
      if (data.logo_proxy_url || data.logo_url) {
        setCurrentLogo(data.logo_proxy_url || data.logo_url);
      }
      if (data.logo_mini_proxy_url || data.logo_mini_url) {
        setCurrentMiniLogo(data.logo_mini_proxy_url || data.logo_mini_url);
        updateFavicon(data.logo_mini_proxy_url || data.logo_mini_url);
      }
    } catch (error) {
      console.error('Error fetching system logo:', error);
    }
  }, []);

  useEffect(() => {
    fetchSystemLogo();
  }, [fetchSystemLogo]);

  // Upload main logo
  const uploadLogo = useCallback(async (file: File) => {
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/config/system/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setCurrentLogo(response.data.logo_url);
      setLogoPreview(null);
      msg.success(MESSAGES.CONFIG.LOGO_UPDATED);

      window.location.reload();
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao fazer upload do logo';
      msg.error(MESSAGES.CONFIG.LOGO_UPLOAD_ERROR(errorMessage));
      setLogoPreview(null);
    } finally {
      setIsUploadingLogo(false);
    }
  }, []);

  // Handle main logo file change
  const handleLogoFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        msg.warning(MESSAGES.CONFIG.LOGO_INVALID_TYPE);
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        msg.warning(MESSAGES.CONFIG.LOGO_TOO_LARGE);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      uploadLogo(file);
    }
  }, [uploadLogo]);

  // Delete main logo
  const handleDeleteLogo = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Remover Logo',
      message: MESSAGES.CONFIG.LOGO_CONFIRM_DELETE,
      variant: 'danger',
      confirmText: 'Remover',
    });

    if (!confirmed) return;

    try {
      await apiClient.delete('/config/system/logo');
      setCurrentLogo(null);
      setLogoPreview(null);
      msg.success(MESSAGES.CONFIG.LOGO_DELETED);
      window.location.reload();
    } catch (error: any) {
      console.error('Error deleting logo:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao remover o logo';
      msg.error(MESSAGES.CONFIG.LOGO_DELETE_ERROR(errorMessage));
    }
  }, [confirm]);

  // Upload mini logo
  const uploadMiniLogo = useCallback(async (file: File) => {
    setIsUploadingMiniLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/config/system/logo-mini', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setCurrentMiniLogo(response.data.logo_mini_url);
      setMiniLogoPreview(null);
      updateFavicon(response.data.logo_mini_url);
      msg.success(MESSAGES.CONFIG.MINI_LOGO_UPDATED);

      window.location.reload();
    } catch (error: any) {
      console.error('Error uploading mini logo:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao fazer upload do mini logo';
      msg.error(MESSAGES.CONFIG.MINI_LOGO_UPLOAD_ERROR(errorMessage));
      setMiniLogoPreview(null);
    } finally {
      setIsUploadingMiniLogo(false);
    }
  }, []);

  // Handle mini logo file change
  const handleMiniLogoFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        msg.warning(MESSAGES.CONFIG.MINI_LOGO_INVALID_TYPE);
        return;
      }

      if (file.size > 1 * 1024 * 1024) {
        msg.warning(MESSAGES.CONFIG.MINI_LOGO_TOO_LARGE);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setMiniLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      uploadMiniLogo(file);
    }
  }, [uploadMiniLogo]);

  // Delete mini logo
  const handleDeleteMiniLogo = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Remover Mini Logo',
      message: MESSAGES.CONFIG.MINI_LOGO_CONFIRM_DELETE,
      variant: 'danger',
      confirmText: 'Remover',
    });

    if (!confirmed) return;

    try {
      await apiClient.delete('/config/system/logo-mini');
      setCurrentMiniLogo(null);
      setMiniLogoPreview(null);
      updateFavicon(null);
      msg.success(MESSAGES.CONFIG.MINI_LOGO_DELETED);
      window.location.reload();
    } catch (error: any) {
      console.error('Error deleting mini logo:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao remover o mini logo';
      msg.error(MESSAGES.CONFIG.MINI_LOGO_DELETE_ERROR(errorMessage));
    }
  }, [confirm]);

  return {
    // Main logo
    logoPreview,
    currentLogo,
    isUploadingLogo,
    fileInputRef,
    handleLogoFileChange,
    handleDeleteLogo,

    // Mini logo
    miniLogoPreview,
    currentMiniLogo,
    isUploadingMiniLogo,
    miniLogoFileInputRef,
    handleMiniLogoFileChange,
    handleDeleteMiniLogo,

    // Confirm dialog
    ConfirmDialog,
  };
}
